import { mergeProps } from '@base-ui/react/merge-props'
import { useRender } from '@base-ui/react/use-render'
import { AnimatePresence, motion } from 'motion/react'
import { createContext, useContext, useId, useState } from 'react'
import { cn } from '#lib/utils'

/*
 * The sliding background of Aceternity UI's card hover effect
 * (ui.aceternity.com/components/card-hover-effect), lifted out of its card
 * grid so any list can use it: one highlight glides to whichever item of the
 * group is hovered, instead of each item lighting up on its own.
 *
 * The item must be `relative isolate`: the highlight sits at -z-10 inside it,
 * above the item's own background and below its content.
 */

const HoverEffectContext = createContext<{
	hovered: string | null
	setHovered: (id: string | null) => void
	layoutId: string
} | null>(null)

export function HoverEffectGroup(props: useRender.ComponentProps<'div'>) {
	const [hovered, setHovered] = useState<string | null>(null)
	const layoutId = useId()
	const { render, ...rest } = props

	const element = useRender({
		defaultTagName: 'div',
		render,
		props: mergeProps<'div'>({ onMouseLeave: () => setHovered(null) }, rest)
	})

	return (
		<HoverEffectContext.Provider value={{ hovered, setHovered, layoutId }}>
			{element}
		</HoverEffectContext.Provider>
	)
}

/**
 * Wires one item into its group. Outside a group it returns `null`, so a
 * shared item can fall back to its own hover style.
 */
export function useHoverEffect(id: string, className?: string) {
	const group = useContext(HoverEffectContext)
	if (!group) {
		return null
	}

	return {
		onMouseEnter: () => group.setHovered(id),
		highlight: (
			<AnimatePresence>
				{group.hovered === id && (
					<motion.span
						animate={{ opacity: 1, transition: { duration: 0.15 } }}
						aria-hidden='true'
						className={cn(
							'absolute inset-0 -z-10 rounded-[inherit]',
							className
						)}
						exit={{
							opacity: 0,
							transition: { duration: 0.15, delay: 0.1 }
						}}
						initial={{ opacity: 0 }}
						layoutId={group.layoutId}
						transition={{
							type: 'spring',
							stiffness: 500,
							damping: 40
						}}
					/>
				)}
			</AnimatePresence>
		)
	}
}
